// LoffaTemplates.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { LGrid, LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit, faCopy, faSpinner } from "@fortawesome/free-solid-svg-icons";
import styles from "../TemplateManagement.module.css"
import { createPortal } from "react-dom";
import { AuthContext } from "../../../../../../context/AuthContext";
import { DeleteConfirmation } from "../TemplateManagementModals/SharedModals/DeleteConfirmationModal";
import CreateLoffaTemplateModal from "../TemplateManagementModals/LoffaTemplateModals/CreateLoffaTemplateModal";
import EditLoffaTemplateModal from "../TemplateManagementModals/LoffaTemplateModals/EditLoffaTemplateModal";
const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE

function LoffaTemplates({loffaTemplates, fetchLoffaTemplates}) {
  const [data, setData] = useState();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState(null);
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);
  const token = authContext.authState().accessToken.accessToken
  const handleDelete = async (templateId) => {
    try {
      const response = await fetch(
        `${GATEWAY_BASE_URL}/datamanager/api/Template/DataTemplate/${templateId}/`, 
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete template.");
      setData(prev => prev.filter(item => item.templateId !== templateId));
      setError(null);
      setItemToDelete(null);
      await fetchLoffaTemplates()
    } catch (error) {
      setError("Error deleting template: " + error.message);
      setItemToDelete(null);
    }
  };

  //handles fetching loffa templates and attaching product name to the response
  const mapLoffaTemplateTableData = async() => {
    setLoading(true);
    try{
      const productDataUrl = GATEWAY_BASE_URL + "/multitenancy/api/ProductType/";
      const productDetailResponse = await fetch(productDataUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!productDetailResponse.ok) throw new Error("Failed to fetch ProductTypes.");
      const productDetailResult = await productDetailResponse.json();
      const { productTypes } = productDetailResult;
      setProducts(productTypes)
      const combined = loffaTemplates.map((t) => {
        const productType = productTypes?.find((p) => p.id === t.productType);
        return{
          ...t,
          productName: productType?.name
        }
      })
      setData(combined)
    }catch(er){
      setError("Error loading data: " + error.message);
    }finally{
      setLoading(false)
    }
  }

  useEffect(() => {
    if (Array.isArray(loffaTemplates) && loffaTemplates.length) {
      mapLoffaTemplateTableData();
    }
  }, [loffaTemplates, token]);

  const loadingPanelMarkup = (
    <div className={styles.templateManagerGridLoadSpinner}>
        <FontAwesomeIcon icon = {faSpinner} spin/>
    </div>
);

  const LoadingPanel = (props) => {
    const { gridRef } = props;
    const gridContent = gridRef.current && gridRef.current.querySelector('.k-grid-content');
    return gridContent ? createPortal(loadingPanelMarkup, gridContent) : loadingPanelMarkup;
  };

  const handleCreateTemplateModalClose = async() =>{
    setShowAddModal(false)
    await fetchLoffaTemplates()
  }

  const handleDuplicate = async (templateId) => {
    try {
      setLoading(true);
      //fetches the template to duplicate
      const getResponse = await fetch(`${GATEWAY_BASE_URL}/datamanager/api/Template/DataTemplate/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!getResponse.ok){throw new Error("Failed to fetch template details.");}
      const templateData = await getResponse.json();
      //build duplicate template
      //rmoves original templateId and appends "(copy)" to template name
      const duplicatePayload = {
        productType: templateData.productType,
        name: `${templateData.name} (copy)`,
        dataTemplateColumns: templateData.dataTemplateColumns.map((col) => ({
          name: col.name,
          type: col.type,
          required: col.required,
          index: col.index,
          ...((col.type !== "DateTime" && col.type !== "Boolean") && {
            minLength: col.minLength != null ? col.minLength : 0,
            maxLength: col.maxLength != null ? col.maxLength : 1000,
          }),
        })),
      };

      await fetch(`${GATEWAY_BASE_URL}/datamanager/api/Template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(duplicatePayload),
        }
      );

      await fetchLoffaTemplates();
      setError(null);
    } catch (error) {
      setError("Error duplicating template: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "actions",
      title: "Actions",
      width: 125,
      cell: (props) => (
        <td className={styles.templateManagerActionButtons}>
          <button 
            className={styles.templateManagerEditButton} 
            onClick={() => setItemToEdit(props.dataItem)}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button 
            className={styles.templateManagerDuplicateButton} 
            onClick={() => handleDuplicate(props.dataItem?.templateId)}
            title="Duplicate"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
          <button 
            className={styles.templateManagerDeleteButton} 
            onClick={() => setItemToDelete(props.dataItem)}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      ),
      filterable: false,
    },
    { field: "name", title: "Name", width: 400 },
    { field: "productName", title: "Product", width: 400 },
  ];

  return (
    <div className={styles.templateManagerContainer}>
      <div className={styles.templateManagerHeader}>
        <h2>Loffa Templates</h2>
        {/* button triggers modal for creating a new loffa template  */}
        <LButton 
          onClick={() => setShowAddModal(true)} 
          themeColor="primary"
          className="green">
          <span className={styles.templateManagerAddButton}>
            <FontAwesomeIcon icon={faPlus} className={styles.templateManagerButtonIcon} />
            <span>Create Template</span>
          </span>
        </LButton>
      </div>

      {error && <div className={styles.templateManagerError}>{error}</div>}
      
      {/* grid displaying official loffa templates  */}
      <div ref={gridRef}>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <LGrid
          resizable={true}
          data={loading ? [{ id: 'loading-dummy' }] : data}
          columns={columns}
          sortable
          filterable
          pageable
          style={{ minHeight: "400px" }}
          className={`${styles.templateManagerGrid} ${loading ? styles.gridLoadingState : ''}`}
        />
      </div>

      <DeleteConfirmation
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={() => handleDelete(itemToDelete?.templateId)}
        title="Delete Loffa Template"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
      />

      {/* modal for creating an official loffa template  */}
      <CreateLoffaTemplateModal
        isOpen={showAddModal} 
        onClose={handleCreateTemplateModalClose}
        authToken={authContext.authState().accessToken.accessToken}
        products={products}  
       />

    {/* modal for editting a LoffaTemplate  */}
    {itemToEdit && (
      <EditLoffaTemplateModal
        isOpen={!!itemToEdit}
        onClose={() => setItemToEdit(null)}
        authToken={authContext.authState().accessToken.accessToken}
        templateId={itemToEdit.templateId}
        products={products}
        refreshTemplates={fetchLoffaTemplates}
      />
    )}
    </div>
  );
}

export { LoffaTemplates };